import React from 'react';
import useAdminAddressLocation from './hooks/useAdminAddressLocation';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Row,
  Spinner,
} from 'reactstrap';
import AdminPage from '../../components/admin/AdminPage';
import { useNavigate, useParams } from 'react-router-dom';
import { buildFullAddress } from './adapters/propertyLocationAdapter';
import AddressListPicker from '../../components/address/AddressListPicker';
import LoadingCard from '../../components/LoadingCard';
import { Controller } from 'react-hook-form';
import NearbyPlacesCard from './components/Location/NearbyPlacesCard';

const AdminPropertyAddressLocation = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const {
    isLoading,
    control,
    setValue,
    errors,
    currentAddress,
    handleSubmit,
    watch,
    formUpdateData,
    doAction,
  } = useAdminAddressLocation({ id });

  // Vista previa en vivo del texto de la web: la dirección sale de lo que se escribe; los nombres
  // de municipio/departamento, de la ubicación ya guardada (cambian al guardar otra).
  const previewAddress = buildFullAddress(
    watch('address'),
    formUpdateData?.location?.relatives,
  );

  if (!formUpdateData) {
    return (
      <LoadingCard name={'Actualizar ubicación'} parent_name={'Propiedades'} />
    );
  }

  return (
    <AdminPage
      name={`Actualizar ubicación a propiedad #${formUpdateData?.code ?? ''}`}
      parentName={'Propiedades'}
      header={<h3 className="mb-0">{'Seleccione los datos de ubicación'}</h3>}
    >
      <Form role="form" onSubmit={handleSubmit(doAction)}>
        <AddressListPicker
          layout="split"
          autoAssign
          defaultPolygonId={formUpdateData.location?.id}
          defaultCenter={{
            lat: formUpdateData?.lat ?? 0,
            lng: formUpdateData?.lng ?? 0,
          }}
          onAddressAssigned={(addressAssignedId, centerPoint) => {
            setValue('lat', centerPoint.lat);
            setValue('lng', centerPoint.lng);
            setValue('address_id', addressAssignedId);
          }}
        />
        <h5 className="text-danger">
          {errors.lat?.message ||
            errors.lng?.message ||
            errors.address_id?.message}
        </h5>

        <h5 className="text-uppercase text-muted ls-1 mb-3 mt-4 border-bottom pb-2">
          Dirección
        </h5>
        <Row>
          {/* address */}
          <Col md="6">
            <FormGroup>
              <label className="form-control-label" htmlFor="address">
                Dirección
              </label>
              <Controller
                name="address"
                control={control}
                defaultValue={''}
                render={({ field }) => (
                  <Input
                    className={`text-dark ${
                      errors.address?.message ? 'is-invalid' : ''
                    }`}
                    placeholder="Pasaje, casa o referencia. Ej: Pasaje 3, casa #12"
                    {...field}
                    id="address"
                  />
                )}
              />
              <h5 className="text-danger">{errors.address?.message}</h5>
            </FormGroup>
          </Col>
        </Row>

        {/* Vista previa del texto que la web arma para la sección "Ubicación" (misma regla). */}
        <div className="rounded p-3 mb-2" style={{ background: '#f6f9fc', border: '1px solid #e9ecef' }}>
          <h5 className="text-uppercase text-muted ls-1 mb-1">
            Así se mostrará la ubicación en la página web
          </h5>
          <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
            {previewAddress || 'Aún no hay datos suficientes para armarla.'}
          </p>
          <small style={{ color: '#525f7f' }}>
            Se arma con la dirección y la ubicación seleccionada, en este orden: dirección, torre, colonia o
            residencial, municipio, departamento y país. Lo repetido se omite. La dirección se ve en
            vivo; el municipio y departamento cambian al
            guardar otra ubicación.
          </small>
        </div>

        <div className="text-right border-top pt-3 mt-4">
          <Button
            type="button"
            color="secondary"
            outline
            className="mr-2"
            onClick={() => navigate(`/properties/admin/${id}`)}
          >
            Cancelar
          </Button>
          <Button color="primary" disabled={isLoading}>
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Actualizar
          </Button>
        </div>
      </Form>

      {/* Fuera del <Form>: tiene sus propios botones y su propio guardado. */}
      <NearbyPlacesCard
        propertyId={id}
        center={
          formUpdateData?.lat && formUpdateData?.lng
            ? { lat: formUpdateData.lat, lng: formUpdateData.lng }
            : undefined
        }
      />
    </AdminPage>
  );
};

export default AdminPropertyAddressLocation;
